import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import {
  Archive,
  Shield,
  AlertTriangle,
  Check,
  Plus,
  Trash2,
  Lock,
  Unlock,
  FileText,
  Calendar,
  History,
  ChevronDown,
  ChevronUp,
  Filter,
} from 'lucide-react';

interface LegalHold {
  Id: string;
  CaseNumber: string;
  EvidenceId?: string;
  HoldName: string;
  HoldReason: string;
  OrderReference?: string;
  IssuedBy: string;
  IssuedAt: string;
  ExpiresAt?: string;
  ReleasedAt?: string;
  ReleasedBy?: string;
  ReleaseReason?: string;
  IsActive: number;
  CaseTitle?: string;
  FileName?: string;
  EvidenceTitle?: string;
}

interface DispositionEvidence {
  Id: string;
  CaseNumber: string;
  FileName: string;
  Title: string;
  EvidenceType: string;
  DispositionStatus: string;
  RetentionDate?: string;
  CreatedAt: string;
  DisposedAt?: string;
  DisposedBy?: string;
  CaseTitle?: string;
  ActiveHoldCount: number;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    Active: 'bg-green-100 text-green-700',
    OnHold: 'bg-amber-100 text-amber-700',
    PendingDisposal: 'bg-red-100 text-red-700',
    Archived: 'bg-blue-100 text-blue-700',
    Disposed: 'bg-gray-100 text-gray-700',
  };

  const icons: Record<string, React.ReactNode> = {
    Active: <Check className="h-3 w-3" />,
    OnHold: <Lock className="h-3 w-3" />,
    PendingDisposal: <AlertTriangle className="h-3 w-3" />,
    Archived: <Archive className="h-3 w-3" />,
    Disposed: <Trash2 className="h-3 w-3" />,
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {icons[status]}
      {status}
    </span>
  );
}

function StatCard({ title, value, icon: Icon, color }: { title: string; value: number; icon: React.ElementType; color: string }) {
  return (
    <div className={`${color} rounded-xl p-4`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <Icon className="h-8 w-8 opacity-50" />
      </div>
    </div>
  );
}

export default function Disposition() {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState<'dashboard' | 'evidence' | 'holds' | 'log'>('dashboard');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState<LegalHold | null>(null);
  const [showArchiveModal, setShowArchiveModal] = useState<DispositionEvidence | null>(null);
  const [showDisposeModal, setShowDisposeModal] = useState<DispositionEvidence | null>(null);
  const [expandedEvidence, setExpandedEvidence] = useState<string | null>(null);

  // Form state for new legal hold
  const [newHold, setNewHold] = useState({
    caseNumber: '',
    evidenceId: '',
    holdName: '',
    holdReason: '',
    orderReference: '',
  });

  const [releaseReason, setReleaseReason] = useState('');
  const [archiveReason, setArchiveReason] = useState('');
  const [disposeReason, setDisposeReason] = useState('');

  // Fetch dashboard data
  const { data: dashboard } = useQuery({
    queryKey: ['disposition-dashboard'],
    queryFn: () => apiClient.getDispositionDashboard(),
  });

  // Fetch evidence list
  const { data: evidence, isLoading: evidenceLoading } = useQuery({
    queryKey: ['disposition-evidence', statusFilter],
    queryFn: () => apiClient.getDispositionEvidence(statusFilter ? { status: statusFilter } : undefined),
    enabled: selectedTab === 'evidence' || selectedTab === 'dashboard',
  });

  // Fetch legal holds
  const { data: holds, isLoading: holdsLoading } = useQuery({
    queryKey: ['legal-holds'],
    queryFn: () => apiClient.getLegalHolds(),
    enabled: selectedTab === 'holds' || selectedTab === 'dashboard',
  });

  // Fetch disposition log
  const { data: logs } = useQuery({
    queryKey: ['disposition-log'],
    queryFn: () => apiClient.getDispositionLog(),
    enabled: selectedTab === 'log',
  });

  // Fetch cases for hold form
  const { data: cases } = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiClient.getCases(),
  });

  // Mutations
  const applyHoldMutation = useMutation({
    mutationFn: () => apiClient.applyLegalHold({
      caseNumber: newHold.caseNumber,
      evidenceId: newHold.evidenceId || undefined,
      holdName: newHold.holdName,
      holdReason: newHold.holdReason,
      orderReference: newHold.orderReference || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['disposition-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['legal-holds'] });
      queryClient.invalidateQueries({ queryKey: ['disposition-evidence'] });
      setShowHoldModal(false);
      setNewHold({ caseNumber: '', evidenceId: '', holdName: '', holdReason: '', orderReference: '' });
    },
  });

  const releaseHoldMutation = useMutation({
    mutationFn: (holdId: string) => apiClient.releaseLegalHold(holdId, releaseReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['disposition-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['legal-holds'] });
      queryClient.invalidateQueries({ queryKey: ['disposition-evidence'] });
      setShowReleaseModal(null);
      setReleaseReason('');
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (evidenceId: string) => apiClient.archiveEvidence(evidenceId, archiveReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['disposition-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['disposition-evidence'] });
      setShowArchiveModal(null);
      setArchiveReason('');
    },
  });

  const disposeMutation = useMutation({
    mutationFn: (evidenceId: string) => apiClient.disposeEvidence(evidenceId, disposeReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['disposition-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['disposition-evidence'] });
      setShowDisposeModal(null);
      setDisposeReason('');
    },
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDaysUntilRetention = (retentionDate: string) => {
    const days = Math.ceil((new Date(retentionDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Check role access
  if (!hasRole(['CaseManager', 'Judge', 'SystemAdmin'])) {
    return (
      <div className="p-8 text-center">
        <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-700">Access Restricted</h2>
        <p className="text-gray-500 mt-2">Only Case Manager, Judge, and System Admin roles can access Disposition Management.</p>
      </div>
    );
  }

  const stats = dashboard?.stats || { active: 0, onHold: 0, pendingDisposal: 0, archived: 0, disposed: 0, activeHolds: 0 };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-justice-navy">Disposition Management</h1>
          <p className="text-gray-500 mt-1">Manage evidence retention, legal holds, and disposal</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: FileText },
            { id: 'evidence', label: 'Evidence', icon: Archive },
            { id: 'holds', label: 'Legal Holds', icon: Lock },
            { id: 'log', label: 'Audit Log', icon: History },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setSelectedTab(id as typeof selectedTab)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                selectedTab === id
                  ? 'border-justice-gold text-justice-navy'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Dashboard Tab */}
      {selectedTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard title="Active" value={stats.active} icon={Check} color="bg-green-50 text-green-700" />
            <StatCard title="On Hold" value={stats.onHold} icon={Lock} color="bg-amber-50 text-amber-700" />
            <StatCard title="Pending Disposal" value={stats.pendingDisposal} icon={AlertTriangle} color="bg-red-50 text-red-700" />
            <StatCard title="Archived" value={stats.archived} icon={Archive} color="bg-blue-50 text-blue-700" />
            <StatCard title="Disposed" value={stats.disposed} icon={Trash2} color="bg-gray-100 text-gray-700" />
            <StatCard title="Active Holds" value={stats.activeHolds} icon={Shield} color="bg-purple-50 text-purple-700" />
          </div>

          {/* Approaching Retention Alert */}
          {dashboard?.approachingRetention && dashboard.approachingRetention > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">
                  {dashboard.approachingRetention} items approaching retention date
                </p>
                <p className="text-sm text-amber-600">Review these items in the next 90 days</p>
              </div>
              <button
                onClick={() => setSelectedTab('evidence')}
                className="ml-auto px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                Review
              </button>
            </div>
          )}

          {/* Recent Activity */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-justice-navy">Recent Disposition Activity</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {dashboard?.recentActivity?.map((activity: any) => (
                <div key={activity.Id} className="p-4 flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${
                    activity.Action === 'Disposed' ? 'bg-red-100' :
                    activity.Action === 'Archived' ? 'bg-blue-100' :
                    activity.Action === 'LegalHoldApplied' ? 'bg-amber-100' :
                    activity.Action === 'LegalHoldReleased' ? 'bg-green-100' :
                    'bg-gray-100'
                  }`}>
                    {activity.Action === 'Disposed' && <Trash2 className="h-4 w-4 text-red-600" />}
                    {activity.Action === 'Archived' && <Archive className="h-4 w-4 text-blue-600" />}
                    {activity.Action === 'LegalHoldApplied' && <Lock className="h-4 w-4 text-amber-600" />}
                    {activity.Action === 'LegalHoldReleased' && <Unlock className="h-4 w-4 text-green-600" />}
                    {!['Disposed', 'Archived', 'LegalHoldApplied', 'LegalHoldReleased'].includes(activity.Action) && 
                      <History className="h-4 w-4 text-gray-600" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{activity.Action}</p>
                    <p className="text-sm text-gray-500">
                      {activity.FileName || activity.Title || activity.EvidenceId?.substring(0, 8)}
                    </p>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <p>{activity.PerformedBy}</p>
                    <p>{formatDate(activity.PerformedAt)}</p>
                  </div>
                </div>
              ))}
              {(!dashboard?.recentActivity || dashboard.recentActivity.length === 0) && (
                <div className="p-8 text-center text-gray-500">No recent activity</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Evidence Tab */}
      {selectedTab === 'evidence' && (
        <div className="space-y-4">
          {/* Filter */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="OnHold">On Hold</option>
                <option value="PendingDisposal">Pending Disposal</option>
                <option value="Archived">Archived</option>
                <option value="Disposed">Disposed</option>
              </select>
            </div>
          </div>

          {/* Evidence List */}
          <div className="bg-white rounded-xl border border-gray-200">
            {evidenceLoading ? (
              <div className="p-8 text-center text-gray-500">Loading evidence...</div>
            ) : !evidence || evidence.length === 0 ? (
              <div className="p-8 text-center">
                <Archive className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No evidence found with this filter.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {evidence.map((ev: DispositionEvidence) => (
                  <div key={ev.Id} className="p-4">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedEvidence(expandedEvidence === ev.Id ? null : ev.Id)}
                    >
                      <div className="flex items-center gap-4">
                        <FileText className="h-8 w-8 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{ev.FileName}</p>
                          <p className="text-sm text-gray-500">{ev.CaseNumber} - {ev.CaseTitle}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <StatusBadge status={ev.DispositionStatus} />
                        {ev.ActiveHoldCount > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            <Lock className="h-3 w-3" />
                            {ev.ActiveHoldCount} holds
                          </span>
                        )}
                        {ev.RetentionDate && (
                          <span className="text-sm text-gray-500">
                            <Calendar className="h-4 w-4 inline mr-1" />
                            {getDaysUntilRetention(ev.RetentionDate) > 0 
                              ? `${getDaysUntilRetention(ev.RetentionDate)} days remaining`
                              : 'Retention expired'}
                          </span>
                        )}
                        {expandedEvidence === ev.Id ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                      </div>
                    </div>

                    {/* Expanded Actions */}
                    {expandedEvidence === ev.Id && (
                      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-3">
                        {ev.DispositionStatus === 'Active' && ev.ActiveHoldCount === 0 && (
                          <>
                            <button
                              onClick={() => setShowArchiveModal(ev)}
                              className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                            >
                              <Archive className="h-4 w-4 inline mr-1" />
                              Archive
                            </button>
                            {hasRole(['SystemAdmin']) && (!ev.RetentionDate || new Date(ev.RetentionDate) < new Date()) && (
                              <button
                                onClick={() => setShowDisposeModal(ev)}
                                className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                              >
                                <Trash2 className="h-4 w-4 inline mr-1" />
                                Dispose
                              </button>
                            )}
                          </>
                        )}
                        {ev.DispositionStatus !== 'Disposed' && (
                          <span className="text-sm text-gray-500">
                            Created: {formatDate(ev.CreatedAt)}
                          </span>
                        )}
                        {ev.DisposedAt && (
                          <span className="text-sm text-gray-500">
                            Disposed: {formatDate(ev.DisposedAt)} by {ev.DisposedBy}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legal Holds Tab */}
      {selectedTab === 'holds' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowHoldModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-justice-navy text-white rounded-lg hover:bg-justice-navy/90"
            >
              <Plus className="h-4 w-4" />
              Apply Legal Hold
            </button>
          </div>

          <div className="bg-white rounded-xl border border-gray-200">
            {holdsLoading ? (
              <div className="p-8 text-center text-gray-500">Loading holds...</div>
            ) : !holds || holds.length === 0 ? (
              <div className="p-8 text-center">
                <Lock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No legal holds found.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {holds.map((hold: LegalHold) => (
                  <div key={hold.Id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${hold.IsActive ? 'bg-amber-100' : 'bg-gray-100'}`}>
                          {hold.IsActive ? <Lock className="h-5 w-5 text-amber-600" /> : <Unlock className="h-5 w-5 text-gray-500" />}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{hold.HoldName}</p>
                          <p className="text-sm text-gray-600 mt-1">{hold.HoldReason}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span>Case: {hold.CaseNumber}</span>
                            {hold.EvidenceTitle && <span>Evidence: {hold.EvidenceTitle}</span>}
                            {hold.OrderReference && <span>Order: {hold.OrderReference}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          hold.IsActive ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {hold.IsActive ? 'Active' : 'Released'}
                        </span>
                        {hold.IsActive && (
                          <button
                            onClick={() => setShowReleaseModal(hold)}
                            className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                          >
                            Release
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                      <span>Issued by {hold.IssuedBy} on {formatDate(hold.IssuedAt)}</span>
                      {hold.ReleasedAt && <span>Released by {hold.ReleasedBy} on {formatDate(hold.ReleasedAt)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit Log Tab */}
      {selectedTab === 'log' && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-justice-navy">Disposition Audit Log</h2>
          </div>
          {!logs || logs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No disposition activity logged yet.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {logs.map((log: any) => (
                <div key={log.Id} className="p-4 flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${
                    log.Action === 'Disposed' ? 'bg-red-100' :
                    log.Action === 'Archived' ? 'bg-blue-100' :
                    log.Action.includes('Hold') ? 'bg-amber-100' :
                    'bg-gray-100'
                  }`}>
                    <History className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{log.Action}</span>
                      {log.PreviousStatus && log.NewStatus && (
                        <span className="text-sm text-gray-500">
                          {log.PreviousStatus} → {log.NewStatus}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {log.FileName || log.EvidenceTitle} ({log.CaseNumber})
                    </p>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <p>{log.PerformedBy}</p>
                    <p>{formatDate(log.PerformedAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Apply Legal Hold Modal */}
      {showHoldModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-justice-navy">Apply Legal Hold</h3>
              <p className="text-sm text-gray-500 mt-1">Protect evidence from disposal</p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case *</label>
                <select
                  value={newHold.caseNumber}
                  onChange={(e) => setNewHold({ ...newHold, caseNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold"
                >
                  <option value="">Select a case...</option>
                  {cases?.map((c) => (
                    <option key={c.caseNumber} value={c.caseNumber}>{c.caseNumber} - {c.title}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hold Name *</label>
                <input
                  type="text"
                  value={newHold.holdName}
                  onChange={(e) => setNewHold({ ...newHold, holdName: e.target.value })}
                  placeholder="e.g., Appeal Pending - Smith v. State"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                <textarea
                  value={newHold.holdReason}
                  onChange={(e) => setNewHold({ ...newHold, holdReason: e.target.value })}
                  placeholder="Legal justification for the hold..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Court Order Reference</label>
                <input
                  type="text"
                  value={newHold.orderReference}
                  onChange={(e) => setNewHold({ ...newHold, orderReference: e.target.value })}
                  placeholder="e.g., Order No. 2026-CV-1234"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowHoldModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => applyHoldMutation.mutate()}
                disabled={!newHold.caseNumber || !newHold.holdName || !newHold.holdReason || applyHoldMutation.isPending}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {applyHoldMutation.isPending ? 'Applying...' : 'Apply Hold'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Release Hold Modal */}
      {showReleaseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-justice-navy">Release Legal Hold</h3>
              <p className="text-sm text-gray-500 mt-1">"{showReleaseModal.HoldName}"</p>
            </div>

            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Release Reason *</label>
              <textarea
                value={releaseReason}
                onChange={(e) => setReleaseReason(e.target.value)}
                placeholder="Reason for releasing the hold..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold"
              />
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowReleaseModal(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                onClick={() => releaseHoldMutation.mutate(showReleaseModal.Id)}
                disabled={!releaseReason || releaseHoldMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {releaseHoldMutation.isPending ? 'Releasing...' : 'Release Hold'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-justice-navy">Archive Evidence</h3>
              <p className="text-sm text-gray-500 mt-1">"{showArchiveModal.FileName}"</p>
            </div>

            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <textarea
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                placeholder="Reason for archiving..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold"
              />
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowArchiveModal(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                onClick={() => archiveMutation.mutate(showArchiveModal.Id)}
                disabled={archiveMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {archiveMutation.isPending ? 'Archiving...' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dispose Modal */}
      {showDisposeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200 bg-red-50">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-red-600" />
                <div>
                  <h3 className="text-lg font-semibold text-red-800">Dispose Evidence</h3>
                  <p className="text-sm text-red-600">This action is permanent and cannot be undone</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                You are about to permanently dispose of: <strong>{showDisposeModal.FileName}</strong>
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Disposal *</label>
              <textarea
                value={disposeReason}
                onChange={(e) => setDisposeReason(e.target.value)}
                placeholder="Legal justification for disposal..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowDisposeModal(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                onClick={() => disposeMutation.mutate(showDisposeModal.Id)}
                disabled={!disposeReason || disposeMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {disposeMutation.isPending ? 'Disposing...' : 'Confirm Disposal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
